import * as React from 'react';
import ThreeSixtyIcon from '../../../../../../svg/icons/360.svg';
import Image from '../../../../components/image';
import Rotator, { RotatorInstance } from '../utils/rotator';
import { ImageTransformationTemplateEntity, SetItem } from '../../../../types';

interface SetProps {
  set: {
    items: SetItem[];
  };
  className?: string;
  recurrent?: boolean;
  showControls?: boolean;
  disableTouch?: boolean;
  autoplay?: boolean;
  onClose?: () => void;
  transformationTemplates: ImageTransformationTemplateEntity;
}

function Set({
  set: { items },
  className,
  recurrent = false,
  showControls = true,
  disableTouch = false,
  autoplay = false,
  onClose,
  transformationTemplates
}: SetProps): React.ReactElement {
  const setElement = React.useRef<HTMLDivElement | null>(null);
  const controlsElement = React.useRef<HTMLDivElement | null>(null);

  const rotatorInstance = React.useRef<RotatorInstance | null>(null);
  const loadedImagesStack = React.useRef<React.SyntheticEvent<HTMLImageElement, Event>[]>([]);

  const [rotatorIndex, setRotatorIndex] = React.useState<number>(0);
  const [loadedImages, setLoadedImages] = React.useState(false);
  const [readyToDisplay, setReadyToDisplay] = React.useState(false);

  React.useEffect(() => {
    const { current: container } = setElement;

    if (container === null) {
      return (): void => {};
    }

    rotatorInstance.current = Rotator(container, {
      cycle: true,
      recurrent,
      imagesClass: 'image-set__image',
      currentClass: 'image-set__image--current',
      grabbingClass: 'image-set__set--grabbing',
      onShow: ({ current }): void => setRotatorIndex(current)
    });

    const eventMap = {
      mousedown: rotatorInstance.current.handleStart,
      mousemove: rotatorInstance.current.handleMove,
      mouseup: rotatorInstance.current.handleEnd,
      mouseleave: rotatorInstance.current.handleEnd,
      touchstart: rotatorInstance.current.handleStart,
      touchmove: rotatorInstance.current.handleMove,
      touchend: rotatorInstance.current.handleEnd
    };
    Object.entries(eventMap).forEach(([event, action]) => {
      if (!disableTouch) {
        container.addEventListener(event, action);
      }
    });
    window.addEventListener('resize', alignRangeWidthToOneOfImages);

    return (): void => {
      if (rotatorInstance.current) {
        rotatorInstance.current.handleStop();
      }
      Object.entries(eventMap).forEach(([key, value]) => {
        container.removeEventListener(key, value);
      });
      window.removeEventListener('resize', alignRangeWidthToOneOfImages);
    };
  }, []);

  React.useEffect(() => {
    if (loadedImages) {
      alignRangeWidthToOneOfImages();
      setReadyToDisplay(true);
    }
  }, [loadedImages]);

  React.useEffect(() => {
    const timeout =
      readyToDisplay && autoplay && rotatorInstance.current
        ? setTimeout(rotatorInstance.current.handlePlay, 1250)
        : null;
    return (): void => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [readyToDisplay]);

  const handleImageLoading = (event: React.SyntheticEvent<HTMLImageElement, Event>): void => {
    loadedImagesStack.current.push(event);

    if (items.length === loadedImagesStack.current.length) {
      setLoadedImages(true);
    }
  };

  function alignRangeWidthToOneOfImages(): void {
    const mainSetImage = setElement.current?.querySelector('.image-set__image--main');
    const width = mainSetImage ? mainSetImage.getBoundingClientRect().width : null;
    if (controlsElement.current && width) {
      controlsElement.current.style.width = `${width}px`;
    }
  }

  function rotateImage({ target: { value } }: { target: { value: string } }): void {
    if (rotatorInstance.current) {
      rotatorInstance.current.handleShow(Number(value));
    }
  }

  return (
    <div className={`image-set ${className} ${readyToDisplay ? '' : 'visibility-hidden'}`}>
      <div className="image-set__wrapper">
        <div className="image-set__set" ref={setElement}>
          <Image
            src={items[0].src}
            onLoad={handleImageLoading}
            className="image-set__image--main visibility-hidden"
            transformationTemplates={transformationTemplates}
          />
          {items.map(({ src, type }, index) => (
            <Image
              src={src}
              onLoad={handleImageLoading}
              key={`${type}_${index + 1}`}
              className="image-set__image"
              transformationTemplates={transformationTemplates}
            />
          ))}
        </div>
        {showControls && (
          <div className={`image-set__controls ${readyToDisplay ? '' : 'visibility-hidden'}`} ref={controlsElement}>
            <button className="image-set__close" type="button" onClick={onClose}>
              <span className="a11y-invisible-element">Close 360 view</span>
              <ThreeSixtyIcon className="image-set__close-icon" role="presentation" pointerEvents="none" />
              <span className="image-set__close-text">Close</span>
            </button>
            <div className="image-set__rotator">
              <input type="range" min="0" max={items.length - 1} value={rotatorIndex} onChange={rotateImage} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Set;
